import { defineStore } from "pinia";
import { computed, ComputedRef, inject, reactive, ref, Ref, toRaw } from "vue";

import {
  getBreadcrumbElement,
  getPartTree,
  getTreeNodeElement
} from "../utils/treeHelper";
import { useSpaceStore } from "./spaceStore";
import { useMainStore } from "./mainStore";
import { useRoute, useRouter } from "vue-router";

export const useTreeStore = defineStore("treeStore", () => {
  const router = useRouter();
  const route = useRoute();
  const spaceStore = useSpaceStore();
  const mainStore = useMainStore();

  const elementTree: treeElement[] = reactive([]);
  const elementBreadcrumbs: Record<string, number[]> = reactive({});
  const breadcrumpElements: Record<number, breadcrumpElement> = reactive({});
  let c = 0;
  // real location of element
  const elementLocation: Record<string, string> = reactive({});
  const locationDependencies: ComputedRef<Record<string, string[]>> = computed(
    () => {
      return Object.entries(elementLocation).reduce(
        (result, [element_id, location_id]) => {
          if (!result[location_id]) {
            result[location_id] = [];
          }

          result[location_id].push(element_id);
          return result;
        },
        {} as Record<string, string[]>
      );
    }
  );

  function createTree(rootElements: string[]) {
    elementTree.push(...createHierachy(rootElements));
  }

  /** create element hierarchy tree (recursive)
   *
   * @param {string[]} children Array of children ids to be added (start with products)
   * @param {string} locationOfParent parents real location id
   *
   * @returns {{title: string, children: string[], id: string}[]}
   */
  function createHierachy(
    children: string[],
    locationOfParent: string = "",
    breadcrumb: number[] = []
  ): treeElement[] {
    const tree: treeElement[] = [];

    // find part variations
    const variantTreeNodes: Record<string, treeElement> = {};
    const variantBreadcrumbs: Record<string, number> = {};

    children.sort((a, b) => {
      console.log("sort", a, spaceStore.calculationElements[b].is_active_part);
      console.log("sort", b, spaceStore.calculationElements[a].is_active_part);
      return (
        (spaceStore.calculationElements[b].is_active_part || 0) -
        (spaceStore.calculationElements[a].is_active_part || 0)
      );
    });

    for (const childId of children) {
      // create new child tree node
      if (!childId || !spaceStore.calculationElements.hasOwnProperty(childId)) {
        console.warn(
          "[WARNING]",
          "[CreateHierachy]",
          "missing child",
          childId,
          breadcrumb.at(-1)
        );
        continue;
      }

      const child = spaceStore.calculationElements[childId];

      const childTreeNode = getTreeNodeElement(childId, child);
      breadcrumpElements[c] = getBreadcrumbElement(childId, child);
      const childBreadcrumb = c;
      c++;

      elementBreadcrumbs[childId] = [...breadcrumb, childBreadcrumb];

      // get real location of element
      const location = spaceStore.calculationElements[
        childId
      ]?.location?.endsWith("location")
        ? child.location
        : locationOfParent;

      // push child id to location data
      if (location) {
        elementLocation[childId] = location;
      }
      // Special case part
      if (childTreeNode.elementType === "part") {
        const [
          manufacturingTreeNode,
          mBreadcrumb,
          variantTreeNode,
          vBreadcrumb
        ] = getPartTree(child);

        breadcrumpElements[c] = vBreadcrumb;
        const variantBreadcrumb = c;
        c++;
        breadcrumpElements[c] = mBreadcrumb;
        const manufacturingBreadcrumb = c;
        c++;

        if (variantTreeNodes.hasOwnProperty(child.variant)) {
          variantTreeNodes[child.variant].children.push(variantTreeNode);
        } else {
          childTreeNode.children = [variantTreeNode];
          variantTreeNodes[child.variant] = childTreeNode;
          variantBreadcrumbs[child.variant] = childBreadcrumb;
        }

        elementBreadcrumbs[child.id + "_variant"] = [
          ...breadcrumb,
          variantBreadcrumbs[child.variant],
          variantBreadcrumb
        ];

        elementBreadcrumbs[child.id + "_manufacturingStep"] = [
          ...elementBreadcrumbs[child.id + "_variant"],
          manufacturingBreadcrumb
        ];

        const manufacturingStepChildren = createHierachy(
          child.children,
          location,
          elementBreadcrumbs[child.id + "_manufacturingStep"]
        );

        // TODO check if this is working

        manufacturingTreeNode.children = manufacturingStepChildren;
      } else {
        // if current element has children push them recursive
        childTreeNode.children = createHierachy(child.children, location, [
          ...breadcrumb,
          childBreadcrumb
        ]);

        // push elements in original tree
        tree.push(childTreeNode);
      }
    }

    for (const variantId in variantTreeNodes) {
      tree.push(variantTreeNodes[variantId]);
    }

    return tree;
  }

  /** deletes element from tree using parents id
   *
   * @param {string} id id of deleted element
   * @param {string} parent id of parent of deleted element
   */
  async function deleteInHierachy(
    id: string,
    parent: string,
    variant: boolean = false
  ) {
    const changeRoute = id === route.params.nodeId;

    if (!parent) {
      if (
        elementTree.at(0)?.id &&
        elementTree.at(0)?.id !== route.params.nodeId
      ) {
        changeRoute &&
          (await router.push({
            name: "calculations-folder",
            params: { nodeId: elementTree.at(0)?.id }
          }));
      } else {
        changeRoute && (await router.push({ name: "base-data" }));
      }
      elementTree.splice(
        0,
        elementTree.length,
        ...elementTree.filter((child) => child.id !== id)
      );
      return;
    }

    if (!variant && parent?.endsWith("part")) {
      parent = parent.split("_")[0] + "_manufacturingStep";
    }

    console.log("delete in hierarachy", id, parent, variant);

    changeRoute &&
      (await router.push({
        name: `calculations-${parent.split("_")[1]}`,
        params: { nodeId: parent }
      }));

    findInHierarchy(parent, (node) => {
      if (!node) {
        console.log("Parent not found");
        return;
      }
      node.children = node.children.filter((child) => child.id !== id);
    });

    // TODO remove elementLocation values for children (maybe handle directly in elements.remove)

    delete elementLocation[id];
  }

  /** Finds parent node in tree and run callback with this node
   *
   * @param {string | undefined} parent element id of parent
   * @param {hierachyCallback} callback gets called with the node of the parent
   * @param {treeNode[]} currentElements
   * @returns {void} ignore return, only for ending function
   */
  function findInHierarchy(
    id: string | undefined,
    callback: treeCallback,
    currentElements: treeElement[] = elementTree
  ): void {
    if (!id) {
      return callback({ children: elementTree });
    }
    for (const node of currentElements) {
      if (node.id === id) {
        return callback(node);
      } else if (node.children.length) {
        findInHierarchy(id, callback, node.children);
      }
    }
  }

  function addToHierarchy(id: string, parentOriginal: string | undefined) {
    console.log("addToHierachy parent: " + parentOriginal + ";\nid: " + id);
    const parent = parentOriginal?.replace("_part", "_manufacturingStep");
    const parentBreadcrumbs = elementBreadcrumbs[parent] ?? [];

    // get real location of element
    if (parentOriginal) {
      elementLocation[id] =
        elementLocation[parentOriginal] ||
        spaceStore.calculationElements[id].location;
    }

    findInHierarchy(parent, (tree) => {
      console.log("Found in hierarachy", toRaw(tree));
      const treeNode = getTreeNodeElement(
        id,
        spaceStore.calculationElements[id]
      );
      breadcrumpElements[c] = getBreadcrumbElement(
        id,
        spaceStore.calculationElements[id]
      );
      const breadcrumb = c;
      c++;

      elementBreadcrumbs[id] = [...parentBreadcrumbs, breadcrumb];

      const [real_id, type] = id.split("_");
      if (type === "part") {
        const [
          manufacturingTreeNode,
          mBreadcrumb,
          variantTreeNode,
          vBreadcrumb
        ] = getPartTree(spaceStore.calculationElements[id]);

        breadcrumpElements[c] = vBreadcrumb;
        const variantBreadcrumb = c;
        c++;
        breadcrumpElements[c] = mBreadcrumb;
        const manufacturingBreadcrumb = c;
        c++;

        treeNode.children = [variantTreeNode];

        elementBreadcrumbs[real_id + "_variant"] = [
          ...elementBreadcrumbs[id],
          variantBreadcrumb
        ];
        elementBreadcrumbs[real_id + "_manufacturingStep"] = [
          ...elementBreadcrumbs[real_id + "_variant"],
          manufacturingBreadcrumb
        ];
      }

      tree.children.push(treeNode);
    });

    // recursivly add all children
    if (spaceStore.calculationElements[id].children.length > 0) {
      for (const child of spaceStore.calculationElements[id].children) {
        addToHierarchy(child, id);
      }
    }
  }

  function addVariantToHierarchy(id: string, parent: string) {
    console.log("variant addToHierachy parent: " + parent + ";\nid: " + id);

    elementLocation[id] =
      elementLocation[parent] || spaceStore.calculationElements[id].location;

    findInHierarchy(parent, (tree) => {
      console.log("Found in hierarachy", toRaw(tree));

      const [real_id, type] = id.split("_");

      const [manufacturingTreeNode, mBreadcrumb, variantTreeNode, vBreadcrumb] =
        getPartTree(spaceStore.calculationElements[id]);

      breadcrumpElements[c] = vBreadcrumb;
      const variantBreadcrumb = c;
      c++;
      breadcrumpElements[c] = mBreadcrumb;
      const manufacturingBreadcrumb = c;
      c++;
      const treeNode = variantTreeNode;

      elementBreadcrumbs[real_id + "_variant"] = [
        ...elementBreadcrumbs[parent],
        variantBreadcrumb
      ];
      elementBreadcrumbs[real_id + "_manufacturingStep"] = [
        ...elementBreadcrumbs[real_id + "_variant"],
        manufacturingBreadcrumb
      ];

      tree.children.push(treeNode);
    });

    // recursivly add all children
    if (spaceStore.calculationElements[id].children.length > 0) {
      for (const child of spaceStore.calculationElements[id].children) {
        addToHierarchy(child, id);
      }
    }
  }

  /** Change name in tree
   *
   * @param {string} id id of changes element
   */
  function changeInHierachy(id: string) {
    findInHierarchy(id, (node) => {
      node.title = spaceStore.calculationElements[id].name;
    });

    const [real_id, type] = id.split("_");
    if (type === "part") {
      findInHierarchy(real_id + "_variant", (node) => {
        node.title =
          spaceStore.calculationElements[id].variant_name ||
          spaceStore.calculationElements[id].name + " - Variant";
      });
      findInHierarchy(real_id + "_manufacturingStep", (node) => {
        node.title =
          spaceStore.calculationElements[id].manufacturing_step_name ||
          spaceStore.calculationElements[id].name + " - Manufacturing Step";
      });
    }
  }

  function changeActiveVariant(id: string, newId: string) {
    findInHierarchy(id, (node) => {
      node.id = newId;
    });

    const c = elementBreadcrumbs[id].at(-1);
    breadcrumpElements[c] = getBreadcrumbElement(
      newId,
      spaceStore.calculationElements[newId]
    );
  }

  function changeRealLocations(id: string, missing = [], changed = []) {
    const oldLocation = elementLocation[id];

    // set real location according to real location of new location
    elementLocation[id] =
      elementLocation[spaceStore.calculationElements[id].location] ??
      spaceStore.calculationElements[id].location;

    // check if location really changed
    if (oldLocation !== elementLocation[id]) {
      // if it is a part or a labour -> manual changes from user requiered
      changed.push(id);
      if (id.endsWith("_part") || id.endsWith("_labour")) {
        const newElement =
          id +
          "|" +
          (id.endsWith("_part") ? "shift_model_part" : "activity_labour");
        missing.push(newElement);
        spaceStore.missingInputs.push(newElement);
      }
    }

    // change all children
    for (const childId of spaceStore.calculationElements[id].children) {
      changeRealLocations(childId, missing, changed);
    }

    return [changed, missing];
  }

  return {
    elementTree,
    elementBreadcrumbs,
    breadcrumpElements,
    elementLocation,
    locationDependencies,
    createHierachy,
    deleteInHierachy,
    changeInHierachy,
    addToHierarchy,
    addVariantToHierarchy,
    changeRealLocations,
    createTree,
    changeActiveVariant
  };
});
